import React, { useEffect, useState } from "react";

import {
  Modal, Form, Input, Select, Button, notification, Spin,
} from "antd";

import { checkUser, sendForgotPasswordEmail } from "../../api/userConfig";

const UserModal = ({
  open,
  onClose,
  onSubmit,
  initialValues = {},
  isAdding,
  companyList = [],
}) => {
  const [form] = Form.useForm();
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkUserLoading, setCheckUserLoading] = useState(false);
  const [userExistsError, setUserExistsError] = useState("");

  // Forgot password state
  const [isForgotPwd, setIsForgotPwd] = useState(false);
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [forgotPwdLoading, setForgotPwdLoading] = useState(false);

  useEffect(() => {
    if (!isAdding) {
      const initialSelection = initialValues.company_name || [];
      setSelectedCompanies(initialSelection);
      form.setFieldsValue({ ...initialValues, company_name: initialSelection });
      setUserExistsError("");
    } else {
      // Reset form when opening modal for adding new user
      form.resetFields();
      setSelectedCompanies([]);
      setUserExistsError("");
    }
  }, [isAdding, initialValues, form, open]);

  /**
   * Handle email blur - Check if user exists
   */
  const handleEmailBlur = async () => {
    if (!isAdding) {
      return;
    }

    const email = form.getFieldValue("user");
    setUserExistsError(""); // Clear previous error

    if (!email || !email.trim()) {
      return;
    }

    // Validate email format before calling API
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return;
    }

    setCheckUserLoading(true);
    try {
      const selectedCompany = selectedCompanies.length > 0 && selectedCompanies[0] !== "all"
        ? selectedCompanies[0]
        : null;

      const result = await checkUser(email.trim(), selectedCompany);

      if (result.success && result.firebase_exist_status === true) {
        // User exists with Firebase credentials - show warning message in the form field area
        setUserExistsError("This user already has an ESV account.");
        // Clear password field since user already has credentials
        form.setFieldsValue({ password: "" });
      } else if (result.userNotFound) {
        // User not found - clear any errors (this is normal for new users)
        setUserExistsError("");
      } else {
        // Other error - clear the exists error
        setUserExistsError("");
      }
    } catch (error) {
      setUserExistsError("");
      // Error handled in checkUser function
    } finally {
      setCheckUserLoading(false);
    }
  };

  /**
   * Handles changes in the company selection. If the user selects "all" companies or selects all
   * companies without "all", sets the selected companies to ["all"] and the form field value to
   * ["all"]. Otherwise, sets the selected companies to the filtered list of companies without "all"
   * and the form field value to the same list.
   * @param {string[]} value The list of selected company values
   */
  const handleCompanyChange = (value) => {
    const isSelectingAll = value.includes("all");
    const isAllSelected = companyList.every((group) => group.options.every((option) => value.includes(option.value) || value.includes("all")));

    if (isSelectingAll || (isAllSelected && !value.includes("all"))) {
      setSelectedCompanies(["all"]);
      form.setFieldsValue({ company_name: ["all"] });
    } else {
      setSelectedCompanies(value.filter((item) => item !== "all"));
      form.setFieldsValue({ company_name: value.filter((item) => item !== "all") });
    }
  };

  /**
   * Toggle forgot password modal
   */
  const toggleForgotPwd = () => {
    setIsForgotPwd(!isForgotPwd);
  };

  /**
   * Send reset password email
   */
  const sendResetPwdLink = async () => {
    const email = form.getFieldValue("user");
    const emailTrimmed = email?.trim();

    if (!emailTrimmed) {
      notification.error({
        message: "Error",
        description: "Please enter a valid email address",
      });
      return;
    }

    setForgotPwdLoading(true);
    try {
      const result = await sendForgotPasswordEmail(emailTrimmed);
      if (!result.success) {
        notification.error({
          message: "Error",
          description: result.message,
        });
        setForgotPwdLoading(false);
        return;
      }

      setIsForgotPwd(false);
      setIsLinkSent(true);
      setForgotPwdLoading(false);
    } catch (error) {
      setForgotPwdLoading(false);
      notification.error({
        message: "Error",
        description: error.message || "Something went wrong",
      });
    }
  };

  /**
   * Toggle link sent modal
   */
  const toggleLinkSent = () => {
    setIsLinkSent(!isLinkSent);
  };

  /**
   * Handles form submission. If the form is valid, calls the onSubmit function with the
   * formatted values. If the onSubmit function returns a truthy value, resets the form fields and
   * clears the selected companies. If the onSubmit function throws an error or returns a falsy value,
   * shows an error notification and doesn't clear the selected companies.
   */
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      if (!isAdding) {
        delete values.password;
      }
      const formattedValues = {
        user: values.user,
        ...(values.password
          && !userExistsError
          && isAdding
          && { password: values.password }), // Include password only for add flow
        company_access: selectedCompanies.includes("all")
          ? ["all"]
          : selectedCompanies,
      };
      const success = await onSubmit(formattedValues);
      if (success) {
        form.resetFields();
        setSelectedCompanies([]);
        setUserExistsError("");
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      notification.error({
        message: "Invalid Input(s)",
        description: "Please check for missing or incorrect inputs",
      });
    }
  };

  const formattedOptions = [
    {
      label: "All Companies",
      options: [{ label: "All Companies", value: "all" }],
    },
    ...companyList.map((group) => ({
      label: group.label,
      options: group.options.map((option) => ({
        ...option,
        disabled: selectedCompanies.includes("all"),
      })),
    })),
  ];

  /**
   * Render forgot password modal
   */
  const renderForgotPwdModal = () => (
    <Modal
      open
      maskClosable={false}
      closable={false}
      centered
      footer={null}
      width="480px"
      className="forgotPasswordModal"
    >
      <div className="forgotPasswordDel">
        <div className="forgotPasswordDelTitle">Forgot Password?</div>
        <div className="forgotPasswordDelTitleMessage">
          Enter the email address associated with your account
        </div>
        <Input
          className="ForgotPassword__Input"
          type="text"
          value={form.getFieldValue("user") || ""}
          placeholder="Email"
          disabled
        />
        <div className="forgotPasswordDelBtn">
          <Button
            type="default"
            onClick={toggleForgotPwd}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={forgotPwdLoading}
            disabled={forgotPwdLoading}
            onClick={sendResetPwdLink}
          >
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  );

  /**
   * Render link sent confirmation modal
   */
  const renderLinkSentModal = () => (
    <Modal
      open
      closable={false}
      centered
      footer={null}
      width="456px"
      className="linkSentModal"
    >
      <div className="forgotPasswordDel">
        <div className="forgotPasswordDelTitle">Link Sent</div>
        <div className="forgotPasswordDelTitleMessage">
          Follow the link in your email to reset your password.
        </div>
        <div className="forgotPasswordDelBtn">
          <Button
            type="primary"
            onClick={toggleLinkSent}
          >
            Okay
          </Button>
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      <Modal
        title={isAdding ? "Add User" : "Edit User"}
        open={open}
        onCancel={onClose}
        footer={(
          <div className="user-modal-footer">
            {!isAdding && (
              <div className="forgot-password-container">
                <Button
                  type="link"
                  onClick={toggleForgotPwd}
                  className="forgot-password-link"
                >
                  Forgot Password?
                </Button>
              </div>
            )}
            <div className="user-modal-footer-buttons">
              <Button key="cancel" onClick={onClose}>
                Cancel
              </Button>
              <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                {isAdding ? "Add User" : "Save"}
              </Button>
            </div>
          </div>
        )}
        maskClosable={false} // Prevent closing on click outside
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="user"
            label="Email ID"
            validateStatus={userExistsError ? "warning" : ""}
            help={userExistsError || null}
            rules={[
              { required: true, message: "Please input the email ID!" },
              { type: "email", message: "Please enter a valid email address!" },
            ]}
          >
            <Input
              placeholder="Enter email ID"
              type="email"
              onBlur={isAdding ? handleEmailBlur : undefined}
              suffix={isAdding && checkUserLoading ? <Spin size="small" /> : null}
              disabled={!isAdding || loading}
            />
          </Form.Item>
          {isAdding && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: !userExistsError, message: "Please input the password!" },
                {
                  validator: (_, value) => {
                    if (userExistsError || !value || value.length >= 6) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Password must be at least 6 characters long!"));
                  },
                },
              ]}
            >
              <Input.Password
                placeholder="Enter password"
                autoComplete="new-password"
                disabled={userExistsError || loading}
              />
            </Form.Item>
          )}
          <Form.Item
            name="company_name"
            label="Company Name"
            rules={[{ required: true, message: "Please select a company!" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select or add companies"
              options={formattedOptions}
              value={selectedCompanies}
              maxTagCount="responsive"
              onChange={handleCompanyChange}
            />
          </Form.Item>
        </Form>
      </Modal>

      {isForgotPwd && renderForgotPwdModal()}
      {isLinkSent && renderLinkSentModal()}
    </>
  );
};

export default UserModal;
